/**
 * Store Details
 *
 * - Resolves the "Open now" pill on every store-details block.
 * - Lazily loads the Google Maps JavaScript API (shared with the Store
 *   Locator section when both live on the same page) and renders a small
 *   interactive map per block.
 * - Accordion toggling is handled natively by <details>.
 */
(function () {
  'use strict';

  const MAPS_SRC_MATCH = 'maps.googleapis.com/maps/api/js';
  const utils = window.StoreLocatorUtils;

  /* -------------------------------------------------------------------- */
  /* Open-now pill                                                         */
  /* -------------------------------------------------------------------- */

  function applyOpenNowPill(pillEl) {
    const label = pillEl.querySelector('.store-details__open-pill-label');
    if (!label || !utils || typeof utils.isOpenNow !== 'function') return;

    const raw = pillEl.dataset.openingHours;
    if (!raw) return;

    let hours;
    try {
      hours = JSON.parse(raw);
    } catch (e) {
      return;
    }

    const open = utils.isOpenNow(hours);
    if (open === null) return;

    pillEl.classList.remove('store-details__open-pill--unknown');

    if (open) {
      pillEl.classList.add('store-details__open-pill--open');
      label.textContent = pillEl.dataset.labelOpen || 'Open Now';
    } else {
      pillEl.classList.add('store-details__open-pill--closed');
      label.textContent = pillEl.dataset.labelClosed || 'Closed';
    }
  }

  function applyOpenNowPills(root) {
    const pills = (root || document).querySelectorAll('[data-open-pill]');
    for (const pill of pills) {
      applyOpenNowPill(pill);
    }
  }

  /* -------------------------------------------------------------------- */
  /* Opening hours table                                                   */
  /* -------------------------------------------------------------------- */

  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  function populateHoursTable(tableEl) {
    if (!utils || typeof utils.parseOpeningHours !== 'function') return;

    const hours = utils.parseOpeningHours(tableEl.dataset.openingHours);
    if (!hours) return;

    const closedLabel = tableEl.dataset.labelClosed || 'Closed';
    const today = new Date();
    const todayKey = DAYS[today.getDay() === 0 ? 6 : today.getDay() - 1];

    const rows = tableEl.querySelectorAll('[data-hours-row]');
    for (const row of rows) {
      const day = row.dataset.hoursRow;
      const cell = row.querySelector('[data-hours-time]');
      if (!cell) continue;

      const data = hours[day];
      if (data && !data.closed && data.open && data.close) {
        cell.textContent = data.open + ' – ' + data.close;
      } else {
        cell.textContent = closedLabel;
      }

      if (day === todayKey) {
        row.classList.add('store-details__hours-row--today');
      }
    }
  }

  function populateHoursTables(root) {
    const tables = (root || document).querySelectorAll('[data-hours-table]');
    for (const table of tables) {
      populateHoursTable(table);
    }
  }

  /* -------------------------------------------------------------------- */
  /* Map rendering                                                         */
  /* -------------------------------------------------------------------- */

  function renderMap(el) {
    if (!window.google || !window.google.maps || el.dataset.mapReady === '1') return;

    const lat = parseFloat(el.dataset.lat);
    const lng = parseFloat(el.dataset.lng);
    if (isNaN(lat) || isNaN(lng)) return;

    const position = { lat: lat, lng: lng };
    const map = new google.maps.Map(el, {
      center: position,
      zoom: parseInt(el.dataset.zoom, 10) || 15,
      mapTypeId: el.dataset.mapStyle || 'roadmap',
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: 'cooperative'
    });

    new google.maps.Marker({
      position: position,
      map: map,
      title: el.dataset.title || ''
    });

    el.dataset.mapReady = '1';
  }

  function renderAllMaps(root) {
    const canvases = (root || document).querySelectorAll('[data-store-map]');
    for (const canvas of canvases) {
      renderMap(canvas);
    }
  }

  function resolveApiKey(root) {
    const scope = root && root.querySelector ? root : document;
    const section = scope.querySelector('[data-section-type="store-details"][data-maps-api-key]');
    if (section && section.dataset.mapsApiKey) return section.dataset.mapsApiKey;

    // Fall back to the Store Locator's key if it shares the page.
    if (window.storeLocatorConfig && window.storeLocatorConfig.apiKey) {
      return window.storeLocatorConfig.apiKey;
    }

    return null;
  }

  function ensureMapsApi(root, onReady) {
    if (window.google && window.google.maps) {
      onReady();
      return;
    }

    const existing = document.querySelector('script[src*="' + MAPS_SRC_MATCH + '"]');
    if (existing) {
      existing.addEventListener('load', onReady);
      return;
    }

    const apiKey = resolveApiKey(root);
    if (!apiKey) return;

    const script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey) + '&libraries=places,geometry';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onReady);
    document.head.appendChild(script);
  }

  function initMaps(root) {
    const canvases = (root || document).querySelectorAll('[data-store-map]');
    if (canvases.length === 0) return;
    ensureMapsApi(root, function () { renderAllMaps(root); });
  }

  /* -------------------------------------------------------------------- */
  /* Bootstrap                                                             */
  /* -------------------------------------------------------------------- */

  function init(root) {
    applyOpenNowPills(root);
    populateHoursTables(root);
    initMaps(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector) {
      init(event.target);
    }
  });
})();
